import { useSetAtom } from 'jotai';
import React, { useCallback } from 'react';

import { Chip, Name, Table, Text } from '~/components/blocks';
import { useMedicationFollowupSettingStatus } from '~/components/layouts/MessageSettings/use-medication-followup-setting-status';
import { ContactMethod } from '~/components/pages/Patient/AllPatientList/AllPatient/ContactMethod';
import { WebVisitorOnAllPatientListFragment } from '~/graphql/generated/graphql';
import { patientDetailDialogState } from '~/state/partials/patient_detail_dialog/atoms';
import { TabType } from '~/state/partials/patient_detail_dialog/types';
import { Label } from '~/utils/label';

import { CommentColumn } from './CommentColumn';
import { MessageSendAtColumn } from './MessageSendAtColumn';
import { MessageSendFromColumn } from './MessageSendFromColumn';
import { QuestionnaireSheetColumn } from './QuestionnaireSheetColumn';
import { useSamePatient } from './SamePatientModal/use-same_patient';

type Props = {
  webVisitor: WebVisitorOnAllPatientListFragment;
};

export const WebVisitor = React.memo(({ webVisitor }: Props) => {
  const setPatientDetailDialogState = useSetAtom(patientDetailDialogState);
  const now = new Date();
  const latestFollowupMessage =
    webVisitor.medicationFollowupMessages.nodes.find((m) => !m.isAutoReplyMessage) || null;

  // すでに送信されたメッセージの最新を取得する
  const latestSentFollowupMessage =
    webVisitor.medicationFollowupMessages.nodes.find(
      (m) => !m.isAutoReplyMessage && m.status === 'sent' && new Date(m.sendAt) < now,
    ) || null;
  const latestSentFollowupAutoReplyMessage =
    webVisitor.medicationFollowupMessages.nodes.find(
      (m) => m.isAutoReplyMessage && m.status === 'sent' && new Date(m.sendAt) < now,
    ) || null;

  const { statusManagement } = useMedicationFollowupSettingStatus();
  const medixsPatient = webVisitor.medixsPatient;
  const medicationFollowupStatus =
    medixsPatient?.medicationFollowupStatus || webVisitor.medicationFollowupStatus;
  const isFollowingUp = medicationFollowupStatus === 'in_progress';

  const { getSamePatientByGuestPatient } = useSamePatient(webVisitor.id);
  const handleClick = useCallback(async () => {
    if (medixsPatient) {
      const guestPatient = medixsPatient.guestPatients[0];
      setPatientDetailDialogState((state) => ({
        ...state,
        isOpen: true,
        type: 'message' as TabType,
        patientId: guestPatient.id,
      }));
    } else {
      await getSamePatientByGuestPatient();
    }
  }, [getSamePatientByGuestPatient, medixsPatient, setPatientDetailDialogState]);

  const hasLineVisitor = medixsPatient?.guestPatients.some(
    (guestPatient) => guestPatient.__typename === 'LineVisitor' && guestPatient.active,
  );

  return (
    <Table.Tr onClick={handleClick}>
      <Table.Td>
        <Name
          familyName={webVisitor.familyName}
          givenName={webVisitor.givenName}
          phoneticFamilyName={webVisitor.phoneticFamilyName}
          phoneticGivenName={webVisitor.phoneticGivenName}
        />
      </Table.Td>
      <Table.Td>
        <Text size="m" whiteSpace="nowrap">
          {Label.age(webVisitor.birthDate)}({Label.sex(webVisitor.sex)})
        </Text>
      </Table.Td>
      <Table.Td>
        <ContactMethod
          hasApp={!!webVisitor.medixsPatient?.patient}
          phoneNumber={medixsPatient?.mobilePhone || webVisitor.mobilePhone}
          hasLine={hasLineVisitor}
        />
      </Table.Td>
      <Table.Td>
        <MessageSendAtColumn latestFollowupMessage={latestFollowupMessage} />
      </Table.Td>
      <Table.Td>
        {statusManagement && isFollowingUp && (
          <Chip size="fixed" color="blue" textColor="white">
            対応中
          </Chip>
        )}
      </Table.Td>
      <Table.Td>
        <QuestionnaireSheetColumn
          latestSentFollowupMessage={latestSentFollowupMessage}
          latestSentFollowupAutoReplyMessage={latestSentFollowupAutoReplyMessage}
        />
      </Table.Td>
      <Table.Td>
        <CommentColumn patient={webVisitor} />
      </Table.Td>
      <Table.Td>
        <MessageSendFromColumn latestFollowupMessage={latestFollowupMessage} />
      </Table.Td>
    </Table.Tr>
  );
});

WebVisitor.displayName = 'WebVisitor';
